<template>
  <div class="expense-sheet-items-table">
    <DataTable
      :value="items"
      class="mt-4 custom-table"
      scrollable
      stripedRows
      showGridlines
    >
      <Column :header="t('wkt.client.expensesheets.detail-window.items.ticket_date')" style="width: 15%" field="ticketDate" sortable>
        <template #body="{ data }">
          {{ formatLongDateTime(data.ticketDate) }}
        </template>
      </Column>
      
      <Column :header="t('wkt.client.expensesheets.detail-window.items.expense_description')" style="width: 25%; max-width: 150px" field="expenseDescription" sortable>
        <template #body="{ data }">
          {{ data.expenseDescription }}
        </template>
      </Column>
      
      <Column :header="t('wkt.client.expensesheets.detail-window.items.category')" style="width: 15%" field="categoryName" sortable>
        <template #body="{ data }">
          {{ data.categoryName }}
        </template>
      </Column>
      
      <Column :header="t('wkt.client.expensesheets.detail-window.items.format_type')" style="width: 10%" field="formatType" sortable>
        <template #body="{ data }">
          {{ getFormatTypeText(Number(data.formatType)) }}
        </template>
      </Column>
      
      <Column :header="t('wkt.client.expensesheets.detail-window.items.payment_method')" style="width: 15%" field="paymentMethod" sortable>
        <template #body="{ data }">
          {{ getPaymentMethodText(Number(data.paymentMethod)) }}
        </template>
      </Column>
      
      <Column :header="t('wkt.client.expensesheets.detail-window.items.taxes_percent')" style="width: 5%" field="taxesPercent" sortable>
        <template #body="{ data }">
          {{ formatCurrency(data.taxesPercent) }}%
        </template>
      </Column>
      
      <Column :header="t('wkt.client.expensesheets.detail-window.items.gross_amount')" style="width: 5%" field="grossAmount" sortable>
        <template #body="{ data }">
          {{ formatCurrency(data.grossAmount) }}
        </template>
      </Column>
      
      <Column :header="t('wkt.client.expensesheets.detail-window.items.total_amount')" style="width: 5%" field="totalAmount" sortable>
        <template #body="{ data }">
          {{ formatCurrency(data.totalAmount) }}
        </template>
      </Column>
      
      <Column :header="t('wkt.client.expensesheets.detail-window.items.attachment')" style="max-width: 150px" field="documentName" sortable>
        <template #body="{ data }">
          <a class="document-link" @click.stop="$emit('download-document', data)">
            {{ data.documentName }}
          </a>
        </template>
      </Column>
      
      <Column style="width: 100px">
        <template #body="{ data }">
          <Button
            v-if="[-1, 2].includes(Number(parent.status))"
            :label="t('wkt.client.expensesheets.detail-window.items.button-edit')"
            class="footer-primary-button"
            color="#185EA9"
            variant="text"
            size="small"
            @click.stop="$emit('edit-item', data)"
          />
          <Button
            v-else
            :label="t('wkt.client.expensesheets.detail-window.items.button-view')"
            class="footer-primary-button"
            color="#185EA9"
            variant="text"
            size="small"
            @click.stop="$emit('view-item', data)"
          />
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useDateFormatter } from '@wkt/composables/useDateFormatter'
import { useExpenseSheets } from '@wkt/composables/useExpenseSheets'
import { useUtils } from '@wkt/composables/useUtils'

// Props
interface Props {
  parent: any
  items: any[]
}

defineProps<Props>()

// Emits
defineEmits<{
  'download-document': [item: any]
  'edit-item': [item: any]
  'view-item': [item: any]
}>()

// Composables
const { t } = useI18n()
const { formatLongDateTime } = useDateFormatter()
const { getFormatTypeText, getPaymentMethodText } = useExpenseSheets()
const { formatCurrency } = useUtils()
</script>

<style scoped>
.expense-sheet-items-table {
  margin-top: 1rem;
}

.document-link {
  color: var(--primary-color);
  text-decoration: underline;
  cursor: pointer;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.document-link:hover {
  text-decoration: none;
}
</style>